import { useState, useEffect } from 'preact/hooks';
import { STOREFRONT_QUERIES, transformVideos } from '../lib/storefrontClient';
import { filterAndSortVideos } from '../utils/video-filters';

let cachedThumbnailUrl = null;
let cachedVideo = null;
let cachedPromise = null;
let cacheTimestamp = null;
const CACHE_DURATION = 1 * 60 * 1000; // 1 minute

// Force clear cache function (can be called externally)
export function clearWorkoutImageCache() {
    cachedThumbnailUrl = null;
    cachedVideo = null;
    cachedPromise = null;
    cacheTimestamp = null;
    console.log('🗑️ Workout image cache cleared');
}

/**
 * Custom hook to get the most recent workout image and video data
 * Caches the result to avoid multiple fetches across pages
 */
export function useWorkoutImage() {
    const [thumbnailUrl, setThumbnailUrl] = useState(cachedThumbnailUrl);
    const [video, setVideo] = useState(cachedVideo);
    const [loading, setLoading] = useState(!cachedThumbnailUrl);
    
    useEffect(() => {
        // Check if cache is still valid
        const now = Date.now();
        const cacheExpired = !cacheTimestamp || (now - cacheTimestamp) > CACHE_DURATION;
        
        // If we have a cached URL and it's not expired, use it
        if (cachedThumbnailUrl && !cacheExpired) {
            setThumbnailUrl(cachedThumbnailUrl);
            setVideo(cachedVideo);
            setLoading(false);
            return;
        }
        
        // Clear expired cache
        if (cacheExpired) {
            cachedThumbnailUrl = null;
            cachedVideo = null;
            cachedPromise = null;
            cacheTimestamp = null;
        }
        
        // If there's already a fetch in progress, wait for it
        if (cachedPromise) {
            cachedPromise.then(result => {
                setThumbnailUrl(result.url);
                setVideo(result.video);
                setLoading(false);
            });
            return;
        }
        
        // Start a new fetch
        const fetchImage = async () => {
            const defaultImage = 'https://cdn.shopify.com/s/files/1/0667/9599/5332/files/download-large.png?v=1761729950';
            
            try {
                const shopDomain = window.Shopify?.shop || window.location.hostname;
                const storefrontAccessToken = '4894686455994e9f30ec76fc0246ad24';
                const apiVersion = '2024-01';
                
                const response = await fetch(`https://${shopDomain}/api/${apiVersion}/graphql.json`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
                    },
                    body: JSON.stringify({
                        query: STOREFRONT_QUERIES.GET_VIDEOS,
                        variables: { first: 100 }
                    }),
                });
                
                const result = await response.json();
                
                if (result.data?.metaobjects) {
                    const videos = await transformVideos(result.data.metaobjects);
                    const sortedVideos = filterAndSortVideos(videos, 'All', 'newest', 1, undefined, true);
                    
                    if (sortedVideos.length > 0 && sortedVideos[0].previewImageUrl) {
                        cachedThumbnailUrl = sortedVideos[0].previewImageUrl;
                        cachedVideo = sortedVideos[0];
                        cacheTimestamp = Date.now();
                        return { url: cachedThumbnailUrl, video: cachedVideo };
                    }
                }
                
                cachedThumbnailUrl = defaultImage;
                cachedVideo = null;
                cacheTimestamp = Date.now();
                return { url: defaultImage, video: null };
            } catch (error) {
                console.error('Error fetching workout image:', error);
                cachedThumbnailUrl = defaultImage;
                cachedVideo = null;
                cacheTimestamp = Date.now();
                return { url: defaultImage, video: null };
            }
        };
        
        cachedPromise = fetchImage();
        cachedPromise.then(result => {
            setThumbnailUrl(result.url);
            setVideo(result.video);
            setLoading(false);
            cachedPromise = null;
        });
    }, []);
    
    return { thumbnailUrl, video, loading };
}
